import React, { useEffect, useRef } from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { PieChart3D, PieSeries3D } from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

const PieChart = ({ dataSet = [] }) => {
  const chartRef = useRef(null);
  const legendRef = useRef(null);

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const textColor = rootStyles.getPropertyValue("--text-color").trim() || "#000000";
    const tableBgColor = rootStyles.getPropertyValue("--table-body-bg-color").trim() || "#ffffff";
    const modalBgColor = rootStyles.getPropertyValue("--model-bg-color").trim() || "#ffffff";
    const darkGrayColor = rootStyles.getPropertyValue("--dark-gray-color").trim() || "#727377";
    am4core.useTheme(am4themes_animated);

    // Create chart instance
    const chart = am4core.create(chartRef.current, PieChart3D);
    chart.logo.disabled = true;
    chart.depth = 15; // 3D depth
    chart.angle = 10; // Tilt angle

    // Color mapping
    const colorMap = {
      'Camera': '#bdc3c7',
      'RFID': '#f1c40f',
      'Manual': '#2ecc71'
    };

    // Calculate total for percentages
    const total = dataSet.reduce((sum, item) => sum + item.value, 0);

    // Prepare chart data
    chart.data = dataSet.map(item => ({
      category: item.name,
      value: item.value,
      percent: total > 0 ? Math.round((item.value / total) * 100) : 0,
      color: am4core.color(colorMap[item.name] || '#95a5a6')
    }));

    // Add dummy slice for single value case
    if (dataSet.length === 1) {
      chart.data.push({
        category: '',
        value: 0.001,
        color: am4core.color("#ffffff00")
      });
    }

    // Create pie series
    const pieSeries = chart.series.push(new PieSeries3D());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "category";
    pieSeries.slices.template.propertyFields.fill = "color";

    pieSeries.tooltip.fontSize = 14; 
    pieSeries.tooltip.label.fill = am4core.color("#fff"); 
    pieSeries.tooltip.background.fill = am4core.color(darkGrayColor);
    pieSeries.tooltip.background.cornerRadius = 4;
    pieSeries.tooltip.background.strokeOpacity = 0;

    // Donut appearance
    pieSeries.innerRadius = am4core.percent(40); // Donut

    // Slice styling
    pieSeries.slices.template.stroke = am4core.color(tableBgColor);
    pieSeries.slices.template.strokeWidth = 1;
    pieSeries.slices.template.strokeOpacity = 1;

    // Labels (inside slices)
    pieSeries.labels.template.text = "{percent}%";
    pieSeries.labels.template.radius = am4core.percent(-30);
    pieSeries.labels.template.fill = am4core.color(textColor);
    pieSeries.labels.template.fontSize = 16;
    pieSeries.labels.template.fontWeight = "bold";
    pieSeries.labels.template.horizontalCenter = "middle";
    pieSeries.labels.template.verticalCenter = "middle";
    pieSeries.labels.template.adapter.add("text", (text, target) => {
      const dataItem = target.dataItem;
      if (dataItem && dataItem.values.value.percent < 1) {
        return "";
      }
      return `${Math.round(dataItem.values.value.percent)}%`;
    });

    // Remove outer labels and ticks
    pieSeries.ticks.template.disabled = true;
    pieSeries.alignLabels = false;

    // Custom Legend
    const legendContainer = legendRef.current;
    if (legendContainer) {
      legendContainer.innerHTML = '';

      const legend = document.createElement('div');
      legend.style.display = 'flex';
      legend.style.justifyContent = 'center';
      legend.style.flexWrap = 'wrap';
      legend.style.gap = '10px';
      legend.style.marginTop = '10px';
      legend.style.padding = '5px';
      legend.style.backgroundColor = modalBgColor;
      legend.style.borderRadius = '5px';

      chart.data.filter(item => item.category).forEach(item => {
        const legendItem = document.createElement('div');
        legendItem.style.display = 'flex';
        legendItem.style.alignItems = 'center';
        legendItem.style.gap = '8px';

        const colorBox = document.createElement('div');
        colorBox.style.width = '12px';
        colorBox.style.height = '12px';
        colorBox.style.borderRadius = '3px';
        colorBox.style.backgroundColor = colorMap[item.category] || '#95a5a6';

        const label = document.createElement('span');
        label.textContent = `${item.category}: ${item.value} (${item.percent}%)`;
        label.style.color = textColor;
        label.style.fontSize = '12px';

        legendItem.appendChild(colorBox);
        legendItem.appendChild(label);
        legend.appendChild(legendItem);
      });

      legendContainer.appendChild(legend);
    }

    return () => {
      chart.dispose();
      if (legendRef.current) {
        legendRef.current.innerHTML = '';
      }
    };
  }, [dataSet]);

  return (
    <div style={{ width: '100%' }}>
      <div 
        ref={chartRef} 
        style={{ 
          width: "100%", 
          height: "350px",
          margin: "0 auto"
        }} 
      />
      <div 
        ref={legendRef} 
        style={{
          width: '100%',
          textAlign: 'center'
        }}
      />
    </div>
  );
};

export default PieChart;
